import { Fragment, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { useParams, useOutletContext } from "react-router-dom";
import http from "services/httpClient";
import { getTimezoneOptions } from "utils/timezones";
import { DateTime } from "luxon";
import { ReportParamsModal} from "./ReportParamsModal";
import { ReportRecipientsModal } from "./ReportRecipientsModal";
import type { ReportTemplate, ClientBranding } from "./types";
import type { RootLayoutContextType } from "layouts/RootLayout";

export function ClientReportsPage() {
  const { clientId } = useParams<{ clientId: string }>();

  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">("success");

  // New modal
  const [showNew, setShowNew] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [newErr, setNewErr] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newTime, setNewTime] = useState("08:00");
  const [newFormat, setNewFormat] = useState<"csv" | "pdf">("csv");
  const [newTimezone, setNewTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  );

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReportTemplate | null>(null);

  const [editName, setEditName] = useState("");
  const [editTime, setEditTime] = useState("08:00");
  const [editFormat, setEditFormat] = useState<"csv" | "pdf">("csv");
  const [editTimezone, setEditTimezone] = useState("UTC");

  const timezones = useMemo(() => getTimezoneOptions(DateTime.now()), []);

  const [showParams, setShowParams] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);

  const { setLogoUrl } = useOutletContext<RootLayoutContextType>();

  const fetchTemplates = async () => {
    if (!clientId) return;
    const res = await http.get(`/reports/templates?client_id=${clientId}`);
    setTemplates(res.data || []);
  };

  useEffect(() => {
    fetchTemplates();
  }, [clientId]);

  useEffect(() => {
    const fetchBranding = async () => {
      if (!clientId) {
        setLogoUrl(null);
        return;
      }

      try {
        const res = await http.get(
          `/client_branding/by-client/${clientId}`
        );

        const data = res.data as ClientBranding;
        setLogoUrl(data.logo_path || null);
      } catch {
        // 404 or no branding
        setLogoUrl(null);
      }
    };

    fetchBranding();

    return () => {
      setLogoUrl(null);
    };
  }, [clientId, setLogoUrl]);

  const openNewModal = () => {
    setNewErr(null);
    setNewName("");
    setNewTime("08:00");
    setNewFormat("csv");
    setNewTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    setShowNew(true);
  };

  const createTemplate = async () => {
    if (!clientId) return;
    if (!newName.trim()) {
      setNewErr("Report name is required.");
      return;
    }

    setSavingNew(true);
    setNewErr(null);

    try {
      await http.post("/reports/templates", {
        client_id: Number(clientId),
        name: newName.trim(),
        schedule_time: newTime,
        format: newFormat,
        timezone: newTimezone,
        enabled: true,
      });

      setShowNew(false);
      await fetchTemplates();
      setAlertMessage("Report created.");
      setAlertVariant("success");
    } catch {
      setNewErr("Failed to create report. Please try again.");
    } finally {
      setSavingNew(false);
      setTimeout(() => setAlertMessage(null), 2500);
    }
  };

  const openEditModal = (t: ReportTemplate) => {
    setEditErr(null);
    setSelected(t);

    // backend TIME often returns "08:00:00" → show as "08:00"
    const hhmm = (t.schedule_time || "08:00:00").slice(0, 5);

    setEditName(t.name || "");
    setEditTime(hhmm);
    setEditFormat(t.format || "csv");
    setEditTimezone(t.timezone || "UTC");

    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!selected) return;
    if (!editName.trim()) {
      setEditErr("Report name is required.");
      return;
    }

    setSavingEdit(true);
    setEditErr(null);

    try {
      await http.put(`/reports/templates/${selected.id}`, {
        name: editName.trim(),
        schedule_time: editTime,
        format: editFormat,
        timezone: editTimezone,
      });

      setShowEdit(false);
      await fetchTemplates();
      setAlertMessage("Report updated.");
      setAlertVariant("success");
    } catch {
      setEditErr("Failed to update report. Please try again.");
    } finally {
      setSavingEdit(false);
      setTimeout(() => setAlertMessage(null), 2500);
    }
  };

  const toggleEnabled = async (t: ReportTemplate) => {
    const nextEnabled = !t.enabled;

    // optimistic
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, enabled: nextEnabled } : x)));

    try {
      await http.put(`/reports/templates/${t.id}`, { enabled: nextEnabled });
    } catch {
      // revert
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, enabled: t.enabled } : x)));
      setAlertMessage("Failed to update enabled state.");
      setAlertVariant("danger");
      setTimeout(() => setAlertMessage(null), 2500);
    }
  };

  const handleDeleteTemplate = async (t: ReportTemplate) => {
    if (!window.confirm(`Delete report "${t.name}"? This will also delete its params & recipients.`)) return;

    try {
      await http.delete(`/reports/templates/${t.id}`);
      setAlertMessage("Report deleted successfully.");
      setAlertVariant("success");
      await fetchTemplates();
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.detail || "Failed to delete report. Please try again.";
      setAlertMessage(backendMessage);
      setAlertVariant("danger");
    }

    setTimeout(() => setAlertMessage(null), 3000);
  };

  return (
    <Fragment>
      {/* match DevicesPageList layout */}
      <div className="bg-primary pt-5 pb-21"></div>

      <Container fluid className="mt-n22 px-6">
        <Row className="mt-6">
          <Col md={12} xs={12}>
            <Card className="rounded-3 p-3 shadow-sm">
              <Card.Header className="bg-white py-4 d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Reports</h4>
                <Button className="btn btn-primary btn-sm" onClick={openNewModal}>
                  New Report
                </Button>
              </Card.Header>

              <Table responsive className="text-nowrap mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 90 }}>Enabled</th>
                    <th>Name</th>
                    <th style={{ width: 120 }}>Schedule</th>
                    <th>Timezone</th>
                    <th style={{ width: 90 }}>Format</th>
                    <th style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!t.enabled}
                          onChange={() => toggleEnabled(t)}
                        />
                      </td>
                      <td>{t.name}</td>
                      <td>{t.schedule_time ?? "-"}</td>
                      <td>{t.timezone ?? "-"}</td>
                      <td className="text-uppercase">{t.format ?? "-"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => openEditModal(t)}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-sm btn-secondary me-2"
                          onClick={() => { setSelectedTemplate(t); setShowParams(true); }}
                        >
                          Params
                        </button>

                        <button
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => { setSelectedTemplate(t); setShowRecipients(true); }}
                        >
                          Recipients
                        </button>

                        <button
                          className="btn btn-sm btn-danger ms-2"
                          onClick={() => handleDeleteTemplate(t)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}

                  {templates.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted">
                        No report setting yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <div className="mt-3">
                {alertMessage && (
                  <Alert
                    variant={alertVariant}
                    onClose={() => setAlertMessage(null)}
                    dismissible
                  >
                    {alertMessage}
                  </Alert>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* New Report Modal */}
      <Modal show={showNew} onHide={() => setShowNew(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>New Report</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {newErr && <Alert variant="danger">{newErr}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="AM Report"
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Schedule</Form.Label>
            <Form.Control
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              step={60}   // 60 seconds = minute precision
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Timezone</Form.Label>
            <Form.Select value={newTimezone} onChange={(e) => setNewTimezone(e.target.value)}>
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Format</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="new-fmt-csv"
                label="CSV"
                checked={newFormat === "csv"}
                onChange={() => setNewFormat("csv")}
              />
              <Form.Check
                type="radio"
                id="new-fmt-pdf"
                label="PDF"
                checked={newFormat === "pdf"}
                onChange={() => setNewFormat("pdf")}
              />
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNew(false)} disabled={savingNew}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createTemplate} disabled={savingNew}>
            {savingNew ? "Creating..." : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {clientId && selectedTemplate?.id && (
        <ReportParamsModal
          show={showParams}
          onHide={() => setShowParams(false)}
          clientId={Number(clientId)}
          templateId={selectedTemplate.id}
          templateName={selectedTemplate.name}
          onSaved={fetchTemplates}
        />
      )}

      {clientId && selectedTemplate?.id && (
        <ReportRecipientsModal
          show={showRecipients}
          onHide={() => setShowRecipients(false)}
          clientId={Number(clientId)}
          templateId={selectedTemplate.id}
          templateName={selectedTemplate.name}
          onSaved={fetchTemplates}
        />
      )}

      {/* Edit Report Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Report</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editErr && <Alert variant="danger">{editErr}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="AM Report"
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Schedule</Form.Label>
            <Form.Control
              type="time"
              value={editTime}
              onChange={(e) => setEditTime(e.target.value)}
              step={60}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Timezone</Form.Label>
            <Form.Select value={editTimezone} onChange={(e) => setEditTimezone(e.target.value)}>
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Format</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="edit-fmt-csv"
                label="CSV"
                checked={editFormat === "csv"}
                onChange={() => setEditFormat("csv")}
              />
              <Form.Check
                type="radio"
                id="edit-fmt-pdf"
                label="PDF"
                checked={editFormat === "pdf"}
                onChange={() => setEditFormat("pdf")}
              />
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}

export default ClientReportsPage;